import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface ExportsState {
  /**
   * The export jobs this browser started and is still watching.
   *
   * Persisted so the watch survives a refresh: a sheet of ten thousand rows
   * takes long enough that somebody will reload, switch tabs or wander off
   * while it builds, and the job id has to outlive that or the "it's ready,
   * save it" prompt is lost. The server is the source of truth for a job's
   * status; this is only the list of which ones this tab asked for, so the
   * ready prompt pops for the person who started it rather than for a job a
   * colleague kicked off on another device.
   */
  trackedIds: string[];
  track: (id: string) => void;
  untrack: (id: string) => void;

  /**
   * The job that just turned READY and should prompt to be saved, or null.
   *
   * Held in the store rather than in component state on purpose: the thing that
   * notices a job finished is an effect watching the poll, and setting React
   * state from an effect is the pattern the lint rules here forbid. A store
   * action is an ordinary call, so the effect stays clean and the save popup
   * reads this to decide whether to open. Never persisted - a finished prompt
   * is for right now, not for the next time the app opens.
   */
  justFinishedId: string | null;
  markFinished: (id: string) => void;
  dismissFinished: () => void;
}

export const useExportsStore = create<ExportsState>()(
  persist(
    (set, get) => ({
      trackedIds: [],

      track: (id) => {
        if (!get().trackedIds.includes(id)) {
          set({ trackedIds: [...get().trackedIds, id] });
        }
      },
      untrack: (id) => {
        set({ trackedIds: get().trackedIds.filter((tracked) => tracked !== id) });
      },

      justFinishedId: null,
      markFinished: (id) => {
        set({ justFinishedId: id });
      },
      dismissFinished: () => {
        set({ justFinishedId: null });
      },
    }),
    {
      name: 'windocs.exports',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      partialize: (state) => ({ trackedIds: state.trackedIds }),
    },
  ),
);
